import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { apiURL } from '../API/constants';
import UserPermissionsAPI from '../services/UserPermissionsAPI';
import { extractRawMessageFromData } from '../utils/masterApiError';
import { clearLocalStoragePreservingLanguage } from '../../../i18n';

// Define role hierarchy and permissions (for backward compatibility)
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  OPERATIONS_MANAGER: 'operations_manager',
  CUSTOMER_SERVICE: 'customer_service',
  WAREHOUSE_STAFF: 'warehouse_staff',
  FINANCE_OFFICER: 'finance_officer',
  CUSTOMER: 'customer',
  DRIVER: 'driver',
  SERVICE_MANAGER: 'service_manager',
  SYSTEM_ADMIN: 'system_admin'
};

// Define all available permissions based on the comprehensive backend permissions system
export const PERMISSIONS = {
  // 🔐 Authentication & User Management
  AUTH_LOGOUT: 'auth_logout',
  AUTH_VERIFY: 'auth_verify',
  USER_PERMISSIONS_VIEW: 'user_permissions_view',
  ROLES_LIST: 'roles_list',
  ROLES_VIEW: 'roles_view',
  ROLES_CREATE: 'roles_create',
  ROLES_UPDATE: 'roles_update',
  ROLES_DELETE: 'roles_delete',
  ROLES_ASSIGN_PERMISSIONS: 'roles_assign_permissions',
  ROLES_VIEW_PERMISSIONS: 'roles_view_permissions',
  USER_ROLES_VIEW: 'user_roles_view',
  USER_ROLES_ASSIGN: 'user_roles_assign',
  USER_ROLES_REMOVE: 'user_roles_remove',
  USER_ROLES_REPLACE: 'user_roles_replace',
  /** UserRole assignment records (UserRoleAssignmentViewSet) */
  USER_ROLES_LIST: 'user_roles_list',
  USER_ROLES_CREATE: 'user_roles_create',
  USER_ROLES_UPDATE: 'user_roles_update',
  USER_ROLES_DELETE: 'user_roles_delete',
  PERMISSIONS_LIST: 'permissions_list',
  PERMISSIONS_VIEW: 'permissions_view',
  PERMISSIONS_CREATE: 'permissions_create',
  PERMISSIONS_UPDATE: 'permissions_update',
  PERMISSIONS_DELETE: 'permissions_delete',
  USER_ROLE_ASSIGN: 'user_role_assign',
  USER_ROLE_REVOKE: 'user_role_revoke',
  USERS_LIST: 'users_list',
  USERS_VIEW: 'users_view',
  USERS_CREATE: 'users_create',
  USERS_UPDATE: 'users_update',
  USERS_DELETE: 'users_delete',
  USERS_INVOICED_SHIPMENT_PAYMENT_STATUS: 'users_invoiced_shipment_payment_status',

  // 👥 Customers (8 permissions)
  CUSTOMERS_LIST: 'customers_list',
  CUSTOMERS_VIEW: 'customers_view',
  CUSTOMERS_CREATE: 'customers_create',
  CUSTOMERS_UPDATE: 'customers_update',
  CUSTOMERS_DELETE: 'customers_delete',
  /** Approve or reject customer self-registration (mobile/web signup queue). */
  CUSTOMERS_APPROVE: 'customers_approve',
  /** Enable an existing customer account (POST /api/customers/{id}/activate/). */
  CUSTOMERS_ACTIVATE: 'customers_activate',
  /** Disable an existing customer account (POST /api/customers/{id}/deactivate/). */
  CUSTOMERS_DEACTIVATE: 'customers_deactivate',

  // 📦 Shipments (17 permissions)
  SHIPMENTS_LIST: 'shipments_list',
  SHIPMENTS_VIEW: 'shipments_view',
  SHIPMENTS_CREATE: 'shipments_create',
  SHIPMENTS_UPDATE: 'shipments_update',
  SHIPMENTS_DELETE: 'shipments_delete',
  SHIPMENTS_BULK_UPDATE: 'shipments_bulk_update',
  /** Bulk add / replace / remove tags on multiple shipments (POST /api/shipment/bulk-tags/). */
  SHIPMENTS_BULK_TAGS: 'shipments_bulk_tags',
  SHIPMENTS_BULK_DELETE: 'shipments_bulk_delete',
  /** Scan-update action on a shipment (PUT /shipments/{id}/scan_update/). */
  SHIPMENTS_SCAN_UPDATE: 'shipments_scan_update',
  SHIPMENTS_UPLOAD_EXCEL: 'shipments_upload_excel',
  SHIPMENTS_RECALCULATE_PRICE: 'shipments_recalculate_price',
  SHIPMENTS_APPLIED_RULES_MANAGE: 'shipments_applied_rules_manage',
  SHIPMENTS_BULK_STATUS_UPDATE: 'shipments_bulk_status_update',
  SHIPMENTS_RECEIVE: 'shipments_receive',
  SHIPMENTS_DELETED_LIST: 'shipments_deleted_list',
  SHIPMENTS_RECOVER: 'shipments_recover',
  /** Master catalog (ShipmentType) — granular CRUD; replaces removed shipment_types_manage */
  SHIPMENT_TYPES_LIST: 'shipment_types_list',
  SHIPMENT_TYPES_VIEW: 'shipment_types_view',
  SHIPMENT_TYPES_CREATE: 'shipment_types_create',
  SHIPMENT_TYPES_UPDATE: 'shipment_types_update',
  SHIPMENT_TYPES_DELETE: 'shipment_types_delete',
  RECEIVER_DETAILS_MANAGE: 'receiver_details_manage',
  RECEIVER_DETAILS_LIST: 'receiver_details_list',
  RECEIVER_DETAILS_VIEW: 'receiver_details_view',
  RECEIVER_DETAILS_CREATE: 'receiver_details_create',
  RECEIVER_DETAILS_UPDATE: 'receiver_details_update',
  RECEIVER_DETAILS_DELETE: 'receiver_details_delete',
  CURRENT_LOCATION_MANAGE: 'current_location_manage',
  LOCATIONS_MANAGE: 'locations_manage',
  LOCATIONS_LIST: 'locations_list',
  LOCATIONS_VIEW: 'locations_view',
  LOCATIONS_CREATE: 'locations_create',
  LOCATIONS_UPDATE: 'locations_update',
  LOCATIONS_DELETE: 'locations_delete',
  SHIPMENT_TAGS_CREATE: 'shipment_tags_create',
  SHIPMENT_TAGS_UPDATE: 'shipment_tags_update',
  SHIPMENT_TAGS_DELETE: 'shipment_tags_delete',

  // 📍 Shipment Tracking Events (7 permissions)
  TRACKING_EVENTS_LIST: 'tracking_events_list',
  TRACKING_EVENTS_VIEW: 'tracking_events_view',
  TRACKING_EVENTS_CREATE: 'tracking_events_create',
  TRACKING_EVENTS_UPDATE: 'tracking_events_update',
  TRACKING_EVENTS_DELETE: 'tracking_events_delete',
  TRACKING_EVENTS_BULK_UPDATE: 'tracking_events_bulk_update',
  TRACKING_EVENTS_BULK_DELETE: 'tracking_events_bulk_delete',

  /** Master catalog (ShipmentTrackingEventType) */
  SHIPMENT_TRACKING_EVENT_TYPES_LIST: 'shipment_tracking_event_types_list',
  SHIPMENT_TRACKING_EVENT_TYPES_VIEW: 'shipment_tracking_event_types_view',
  SHIPMENT_TRACKING_EVENT_TYPES_CREATE: 'shipment_tracking_event_types_create',
  SHIPMENT_TRACKING_EVENT_TYPES_UPDATE: 'shipment_tracking_event_types_update',
  SHIPMENT_TRACKING_EVENT_TYPES_DELETE: 'shipment_tracking_event_types_delete',

  /** Master catalog (ShippingCompany) — carriers / forwarders for shipment units */
  SHIPPING_COMPANIES_LIST: 'shipping_companies_list',
  SHIPPING_COMPANIES_VIEW: 'shipping_companies_view',
  SHIPPING_COMPANIES_CREATE: 'shipping_companies_create',
  SHIPPING_COMPANIES_UPDATE: 'shipping_companies_update',
  SHIPPING_COMPANIES_DELETE: 'shipping_companies_delete',

  /** Master catalog (ShippingWarehouse) — origin warehouses for shipment units */
  SHIPPING_WAREHOUSES_LIST: 'shipping_warehouses_list',
  SHIPPING_WAREHOUSES_VIEW: 'shipping_warehouses_view',
  SHIPPING_WAREHOUSES_CREATE: 'shipping_warehouses_create',
  SHIPPING_WAREHOUSES_UPDATE: 'shipping_warehouses_update',
  SHIPPING_WAREHOUSES_DELETE: 'shipping_warehouses_delete',

  /** Shipment units (grouped legs / containers) */
  SHIPMENT_UNITS_LIST: 'shipment_units_list',
  SHIPMENT_UNITS_VIEW: 'shipment_units_view',
  SHIPMENT_UNITS_CREATE: 'shipment_units_create',
  SHIPMENT_UNITS_UPDATE: 'shipment_units_update',
  SHIPMENT_UNITS_DELETE: 'shipment_units_delete',
  /** Append shipments to a unit (POST assign / bulk-assign); separate from full unit PATCH */
  SHIPMENT_UNITS_ASSIGN_SHIPMENTS: 'shipment_units_assign_shipments',

  /** Unit-level tracking events */
  SHIPMENT_UNIT_TRACKING_EVENTS_LIST: 'shipment_unit_tracking_events_list',
  SHIPMENT_UNIT_TRACKING_EVENTS_VIEW: 'shipment_unit_tracking_events_view',
  SHIPMENT_UNIT_TRACKING_EVENTS_CREATE: 'shipment_unit_tracking_events_create',
  SHIPMENT_UNIT_TRACKING_EVENTS_UPDATE: 'shipment_unit_tracking_events_update',
  SHIPMENT_UNIT_TRACKING_EVENTS_DELETE: 'shipment_unit_tracking_events_delete',
  SHIPMENT_UNIT_TRACKING_EVENTS_BULK_UPDATE: 'shipment_unit_tracking_events_bulk_update',
  SHIPMENT_UNIT_TRACKING_EVENTS_BULK_DELETE: 'shipment_unit_tracking_events_bulk_delete',
  /** Scan-update action on a shipment unit (PUT /shipment_unit/{id}/scan_update/). */
  SHIPMENT_UNIT_SCAN_UPDATE: 'shipment_unit_scan_update',

  // 📦 Shipment Pieces — physical packages inside a parent Shipment
  SHIPMENT_PIECES_LIST: 'shipment_pieces_list',
  SHIPMENT_PIECES_VIEW: 'shipment_pieces_view',
  SHIPMENT_PIECES_CREATE: 'shipment_pieces_create',
  SHIPMENT_PIECES_UPDATE: 'shipment_pieces_update',
  SHIPMENT_PIECES_DELETE: 'shipment_pieces_delete',
  /** Bulk soft-recover (POST /shipment_piece/recover/). */
  SHIPMENT_PIECES_RECOVER: 'shipment_pieces_recover',
  /** Single-piece scan-update (PUT /shipment_piece/{id}/scan_update/). */
  SHIPMENT_PIECES_SCAN_UPDATE: 'shipment_pieces_scan_update',
  /** Read-only barcode lookup (POST /shipment_piece/scan_lookup/). */
  SHIPMENT_PIECES_SCAN_LOOKUP: 'shipment_pieces_scan_lookup',
  /** Bulk-scan-update (POST /shipment_piece/bulk_scan_update/). */
  SHIPMENT_PIECES_BULK_SCAN_UPDATE: 'shipment_pieces_bulk_scan_update',
  /** Single-piece label PDF (GET /shipment_piece/{id}/label/). */
  SHIPMENT_PIECES_LABEL: 'shipment_pieces_label',
  /** Batch label PDF (POST /shipment_piece/labels/batch/). */
  SHIPMENT_PIECES_LABEL_BATCH: 'shipment_pieces_label_batch',
  /** Customer view of a single piece (granted to customer role by default). */
  SHIPMENT_PIECES_CUSTOMER_VIEW: 'shipment_pieces_customer_view',
  /** Customer list-by-shipment of pieces (granted to customer role by default). */
  SHIPMENT_PIECES_CUSTOMER_LIST_FOR_SHIPMENT: 'shipment_pieces_customer_list_for_shipment',

  /** Per-piece tracking events */
  SHIPMENT_PIECE_TRACKING_EVENTS_LIST: 'shipment_piece_tracking_events_list',
  SHIPMENT_PIECE_TRACKING_EVENTS_VIEW: 'shipment_piece_tracking_events_view',
  SHIPMENT_PIECE_TRACKING_EVENTS_CREATE: 'shipment_piece_tracking_events_create',
  SHIPMENT_PIECE_TRACKING_EVENTS_UPDATE: 'shipment_piece_tracking_events_update',
  SHIPMENT_PIECE_TRACKING_EVENTS_DELETE: 'shipment_piece_tracking_events_delete',
  SHIPMENT_PIECE_TRACKING_EVENTS_BULK_UPDATE: 'shipment_piece_tracking_events_bulk_update',
  SHIPMENT_PIECE_TRACKING_EVENTS_BULK_DELETE: 'shipment_piece_tracking_events_bulk_delete',

  /** Universal scan resolver (POST /scan/resolve/) — grant to any role with a scan UI. */
  SCAN_RESOLVE: 'scan_resolve',

  // 💵 Pricing rules — unified engine (replaces legacy services / prices permissions)
  PRICING_RULES_LIST: 'pricing_rules_list',
  PRICING_RULES_VIEW: 'pricing_rules_view',
  PRICING_RULES_CREATE: 'pricing_rules_create',
  PRICING_RULES_UPDATE: 'pricing_rules_update',
  PRICING_RULES_DELETE: 'pricing_rules_delete',
  PRICING_RULES_IMPORT: 'pricing_rules_import',

  // 📦 Pricing plans — customer-facing catalog (WhatsApp menu)
  PRICING_PLANS_LIST: 'pricing_plans_list',
  PRICING_PLANS_VIEW: 'pricing_plans_view',
  PRICING_PLANS_CREATE: 'pricing_plans_create',
  PRICING_PLANS_UPDATE: 'pricing_plans_update',
  PRICING_PLANS_DELETE: 'pricing_plans_delete',

  // Settlement groups — how charges settle / separate payment links
  SETTLEMENT_GROUPS_LIST: 'settlement_groups_list',
  SETTLEMENT_GROUPS_VIEW: 'settlement_groups_view',
  SETTLEMENT_GROUPS_CREATE: 'settlement_groups_create',
  SETTLEMENT_GROUPS_UPDATE: 'settlement_groups_update',
  SETTLEMENT_GROUPS_DELETE: 'settlement_groups_delete',

  /**
   * Deprecated names — same codenames as pricing_rules_* (legacy imports).
   * Prefer PRICING_RULES_* in new code.
   */
  PRICING_LIST: 'pricing_rules_list',
  PRICING_VIEW: 'pricing_rules_view',
  PRICING_CREATE: 'pricing_rules_create',
  PRICING_UPDATE: 'pricing_rules_update',
  PRICING_DELETE: 'pricing_rules_delete',
  SERVICES_LIST: 'pricing_rules_list',
  SERVICES_VIEW: 'pricing_rules_view',
  SERVICES_CREATE: 'pricing_rules_create',
  SERVICES_UPDATE: 'pricing_rules_update',
  SERVICES_DELETE: 'pricing_rules_delete',

  // 💳 Payment
  PAYMENTS_LIST: 'payments_list',
  PAYMENTS_VIEW: 'payments_view',
  PAYMENTS_CREATE: 'payments_create',
  PAYMENTS_UPDATE: 'payments_update',
  PAYMENTS_DELETE: 'payments_delete',
  PAYMENTS_ADD_SHIPMENT: 'payments_add_shipment',
  PAYMENTS_ADD_INVOICE: 'payments_add_invoice',
  PAYMENTS_WEBHOOK_THAWANI: 'payments_webhook_thawani',
  PAYMENTS_WEBHOOK_PAYMOB: 'payments_webhook_paymob',
  PAYMENTS_CONFIRM: 'payments_confirm',
  PAYMENTS_REFUND: 'payments_refund',

  // 🏦 Accounts (5 permissions)
  ACCOUNTS_LIST: 'accounts_list',
  ACCOUNTS_VIEW: 'accounts_view',
  ACCOUNTS_CREATE: 'accounts_create',
  ACCOUNTS_UPDATE: 'accounts_update',
  ACCOUNTS_DELETE: 'accounts_delete',

  // 🧾 Invoices (10 permissions)
  INVOICES_LIST: 'invoices_list',
  INVOICES_VIEW: 'invoices_view',
  INVOICES_CREATE: 'invoices_create',
  INVOICES_UPDATE: 'invoices_update',
  INVOICES_DELETE: 'invoices_delete',
  INVOICES_CREATE_BULK: 'invoices_create_bulk',
  INVOICES_GENERATE_FOR_SHIPMENTS: 'invoices_generate_for_shipments',
  INVOICES_REGENERATE: 'invoices_regenerate',
  INVOICES_UPDATE_PAYMENT_STATUS: 'invoices_update_payment_status',

  // ⚙️ Settings (10 permissions)
  SETTINGS_VIEW_PUBLIC: 'settings_view_public',
  SETTINGS_UPDATE_SITE: 'settings_update_site',
  SETTINGS_VIEW_DETAIL: 'settings_view_detail',
  SETTINGS_UPDATE_DETAIL: 'settings_update_detail',
  SETTINGS_DELETE_DETAIL: 'settings_delete_detail',
  SETTINGS_FILES_LIST: 'settings_files_list',
  SETTINGS_FILES_VIEW: 'settings_files_view',
  SETTINGS_FILES_UPLOAD: 'settings_files_upload',
  SETTINGS_FILES_UPDATE: 'settings_files_update',
  SETTINGS_FILES_DELETE: 'settings_files_delete',
  /** Convenience aliases mapping legacy UI constants to the canonical backend codenames. */
  SETTINGS_LIST: 'settings_list',
  SETTINGS_MANAGE: 'settings_update_detail',

  // 📱 WhatsApp (6 permissions)
  WHATSAPP_SEND_SHIPMENT_NOTIFICATION: 'whatsapp_send_shipment_notification',
  WHATSAPP_SEND_SHIPMENT_TEST: 'whatsapp_send_shipment_test',
  WHATSAPP_BULK_SEND_SHIPMENTS: 'whatsapp_bulk_send_shipments',
  WHATSAPP_SEND_INVOICE_NOTIFICATION: 'whatsapp_send_invoice_notification',
  WHATSAPP_BULK_SEND_INVOICES: 'whatsapp_bulk_send_invoices',
  WHATSAPP_RECEIVE_REPLY: 'whatsapp_receive_reply',

  // 📊 Dashboard (1 permission)
  DASHBOARD_VIEW_ANALYTICS: 'dashboard_view_analytics',

  // Legacy permissions for backward compatibility (will be removed in future versions)
  VIEW_DASHBOARD: 'dashboard_view_analytics', // Map to new permission
  VIEW_SHIPMENTS: 'shipments_list', // Map to new permission
  CREATE_SHIPMENTS: 'shipments_create', // Map to new permission
  EDIT_SHIPMENTS: 'shipments_update', // Map to new permission
  DELETE_SHIPMENTS: 'shipments_delete', // Map to new permission
  VIEW_CUSTOMERS: 'customers_list', // Map to new permission
  CREATE_CUSTOMERS: 'customers_create', // Map to new permission
  EDIT_CUSTOMERS: 'customers_update', // Map to new permission
  VIEW_USERS: 'view_users', // Legacy codename (still emitted by backend for some deployments)
  CREATE_USERS: 'create_users', // Legacy codename
  EDIT_USERS: 'edit_users', // Legacy codename
  DELETE_USERS: 'delete_users', // Legacy codename
  VIEW_INVOICES: 'invoices_list', // Map to new permission
  CREATE_INVOICES_LEGACY: 'invoices_create', // Map to new permission
  EDIT_INVOICES: 'invoices_update', // Map to new permission
  VIEW_SERVICES: 'pricing_rules_list',
  CREATE_SERVICES: 'pricing_rules_create',
  EDIT_SERVICES: 'pricing_rules_update',
  SEND_WHATSAPP: 'whatsapp_send_shipment_notification', // Map to new permission
  DASHBOARD_ANALYTICS: 'dashboard_view_analytics', // Map to new permission
  SHIPMENT_UPLOAD: 'shipments_create', // Map to new permission
  RECEIVE_SHIPMENTS: 'shipments_receive', // Map to new permission
  RECALCULATE_SHIPMENTS_PRICE: 'shipments_recalculate_price', // Map to new permission
  ADD_SHIPMENT_PAYMENT: 'payments_add_shipment', // Map to new permission
  ADD_INVOICE_PAYMENT: 'payments_add_invoice', // Map to new permission
  GET_SITE_SETTINGS: 'settings_view_public', // Map to new permission
  UPDATE_SITE_SETTINGS: 'settings_update_site', // Map to new permission
  SETTING_DETAIL: 'settings_view_detail', // Map to new permission
  GET_ALL_SETTING_FILES: 'settings_files_list', // Map to new permission
  GET_SETTING_FILE: 'settings_files_view', // Map to new permission
  UPLOAD_SETTING_FILE: 'settings_files_upload', // Map to new permission
  UPDATE_SETTING_FILE: 'settings_files_update', // Map to new permission
  DELETE_SETTING_FILE: 'settings_files_delete', // Map to new permission
  BULK_SEND_SHIPMENTS_WHATSAPP_MESSAGE: 'whatsapp_bulk_send_shipments', // Map to new permission
  SEND_WHATSAPP_SHIPMENT_NOTIFICATION: 'whatsapp_send_shipment_notification', // Map to new permission
  SEND_WHATSAPP_SHIPMENT_NOTIFICATION_TEST: 'whatsapp_send_shipment_test', // Map to new permission
  LOGOUT: 'auth_logout', // Map to new permission
  USER_ROLE_MANAGEMENT: 'user_roles_view', // Map to new permission
  USER_PERMISSIONS: 'user_permissions_view', // Map to new permission
  ASSIGN_PERMISSIONS: 'roles_assign_permissions', // Map to new permission
  GET_ROLE_PERMISSIONS: 'roles_view_permissions', // Map to new permission
  ASSIGN_ROLE_TO_USER: 'user_role_assign', // Map to new permission
  REVOKE_ROLE: 'user_role_revoke', // Map to new permission
  CREATE_INVOICES: 'invoices_create', // Map to new permission
  GENERATE_INVOICES_FOR_SHIPMENTS: 'invoices_generate_for_shipments', // Map to new permission
  REGENERATE_INVOICES: 'invoices_regenerate', // Map to new permission
  UPDATE_INVOICES_PAYMENT_STATUS: 'invoices_update_payment_status', // Map to new permission
};

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]); // Changed to array for multiple roles
  const [userPermissions, setUserPermissions] = useState([]); // New: array of user permissions
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user permissions from the new API
  const fetchUserPermissions = useCallback(async (token) => {

    try {
      // Check cache first
      const CACHE_KEY = 'user_permissions_cache';
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
      
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          console.log('Using cached user permissions');
          setUserPermissions(data.permissions || []);
          setUserRoles(data.roles ? data.roles.map(role => role.name) : []);
          return data;
        }
      }
      
      // Use the new API service
      const api = new UserPermissionsAPI(apiURL);
      console.log('Fetching user permissions from backend API...');
      
      const data = await api.getUserPermissions(token);
      console.log('User permissions fetched successfully:', data);
      
      // Store permissions and roles
      setUserPermissions(data.permissions || []);
      console.log('User permissions set:', data.permissions);
      setUserRoles(data.roles ? data.roles.map(role => role.name) : []);
      
      // Store in localStorage with cache timestamp
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      localStorage.setItem('user_permissions', JSON.stringify(data.permissions || []));
      localStorage.setItem('user_roles', JSON.stringify(data.roles ? data.roles.map(role => role.name) : []));
      
      return data;
    } catch (error) {
      console.warn('Error fetching user permissions:', error);
      
      if (error.message.includes('401')) {
        console.warn('Unauthorized: Token may be invalid or expired');
      } else if (error.message.includes('404')) {
        console.warn('API endpoint not found: /auth/user-permissions/ may not be implemented');
      } else if (error.message.includes('500')) {
        console.warn('Server error: Backend may be experiencing issues');
      }
      
      // Fallback: if the API is not available, use empty permissions.
      // Keep customer roles when the permissions endpoint rejects inactive portal users.
      setUserPermissions([]);
      localStorage.setItem('user_permissions', JSON.stringify([]));

      let preserveRoles = false;
      try {
        const rawUser = localStorage.getItem('user_data');
        if (rawUser) {
          const parsed = JSON.parse(rawUser);
          const embedded = parsed && parsed.roles;
          if (Array.isArray(embedded)) {
            preserveRoles = embedded.some(
              (role) => (typeof role === 'string' ? role : (role && role.name)) === ROLES.CUSTOMER
            );
          }
        }
      } catch (parseErr) {
        preserveRoles = false;
      }
      if (!preserveRoles) {
        const storedRoles = localStorage.getItem('user_roles');
        if (storedRoles) {
          try {
            const parsedRoles = JSON.parse(storedRoles);
            preserveRoles = Array.isArray(parsedRoles) && parsedRoles.includes(ROLES.CUSTOMER);
          } catch (parseErr) {
            preserveRoles = false;
          }
        }
      }

      if (!preserveRoles) {
        setUserRoles([]);
        localStorage.setItem('user_roles', JSON.stringify([]));
      }
      return null;
    }
  }, []);

  /** Re-sync user from GET /auth/verify/ (e.g. pending-approval polling). Returns true when the user may use the full customer portal. */
  const refreshUserFromVerify = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return false;

      const response = await fetch(`${apiURL}/auth/verify/`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return false;

      const responseText = await response.text();
      if (!responseText.trim()) return false;

      let userData;
      try {
        userData = JSON.parse(responseText);
      } catch (parseErr) {
        return false;
      }

      setUser(userData);
      localStorage.setItem('user_data', JSON.stringify(userData));

      if (userData.roles && Array.isArray(userData.roles)) {
        const roleNames = userData.roles.map((role) => role.name);
        setUserRoles(roleNames);
        localStorage.setItem('user_roles', JSON.stringify(roleNames));
      }

      const roleNames = userData.roles && Array.isArray(userData.roles)
        ? userData.roles.map((role) => role.name)
        : [];
      const isCustomer = roleNames.includes(ROLES.CUSTOMER);
      if (!isCustomer) return true;
      return userData.is_active === true;
    } catch (e) {
      console.warn('refreshUserFromVerify failed:', e);
      return false;
    }
  }, []);

  // Check authentication status
  const checkAuthStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      console.log('Checking auth status, token exists:', !!token);
      
      if (!token) {
        console.log('No token found, setting unauthenticated');
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      // Verify token with backend
      console.log('Verifying token with backend...');
      const response = await fetch(`${apiURL}/auth/verify/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Auth verify response status:', response.status);

      if (response.ok) {
        try {
          const responseText = await response.text();

            
          if (responseText.trim() === '') {
            console.log('Empty response from auth verify, treating as valid');
            // If the backend returns empty response for valid tokens, fetch permissions
            try {
              await fetchUserPermissions(token);
            } catch (error) {
              console.warn('Failed to fetch user permissions during auth verify:', error);
            }
            setIsAuthenticated(true);
          } else {
            try {
              const userData = JSON.parse(responseText);
              console.log('Auth verify successful, user data:', userData);
              // Handle new user data format with roles array
              setUser(userData);
              localStorage.setItem('user_data', JSON.stringify(userData));

              // Extract role names from roles array
              if (userData.roles && Array.isArray(userData.roles)) {
                const roleNames = userData.roles.map(role => role.name);
                setUserRoles(roleNames);
                // Store userRoles in localStorage for persistence
                localStorage.setItem('user_roles', JSON.stringify(roleNames));
              } else {
                // No roles found in user data
                console.warn('No roles found in user data during auth verify');
                setUserRoles([]);
              }
              
              // Fetch permissions from the new API
              try {
                await fetchUserPermissions(token);
              } catch (error) {
                console.warn('Failed to fetch user permissions during auth verify:', error);
              }
              setIsAuthenticated(true);
            } catch (jsonError) {
              console.warn('JSON parsing error in auth verify:', jsonError.message);
              console.log('Response was not valid JSON, treating as valid token anyway');
              console.log('First 100 characters of auth verify response:', responseText.substring(0, 100));
              console.log('Auth verify response starts with:', responseText.charAt(0));
              // If we get a 200 but can't parse JSON, assume the token is valid
              // Fetch permissions from the new API
              try {
                await fetchUserPermissions(token);
              } catch (error) {
                console.warn('Failed to fetch user permissions during auth verify:', error);
              }
              setIsAuthenticated(true);
            }
          }
        } catch (jsonError) {
          console.warn('JSON parsing error:', jsonError.message);
          console.log('Response was not JSON, but status was 200, treating as valid');
          // If we get a 200 but can't parse JSON, assume the token is valid
          // Fetch permissions from the new API
          try {
            await fetchUserPermissions(token);
          } catch (error) {
            console.warn('Failed to fetch user permissions during auth verify:', error);
          }
          setIsAuthenticated(true);
        }
      } else {
        console.log('Auth verify failed, clearing storage');
        // Token invalid, clear storage
        clearLocalStoragePreservingLanguage();
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearLocalStoragePreservingLanguage();
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [fetchUserPermissions]);

  // Check if user is authenticated on mount
  useEffect(() => {
    // Try to restore userRoles, userPermissions and user data from localStorage first
    const storedUserRoles = localStorage.getItem('user_roles');
    const storedUserPermissions = localStorage.getItem('user_permissions');
    const storedUserData = localStorage.getItem('user_data');
    
    if (storedUserRoles) {
      try {
        const parsedUserRoles = JSON.parse(storedUserRoles);
        console.log('Restoring userRoles from localStorage:', parsedUserRoles);
        setUserRoles(parsedUserRoles);
      } catch (e) {
        console.error('Error parsing stored user roles on mount:', e);
        setUserRoles([]);
      }
    } else {
      // No stored roles
      setUserRoles([]);
    }
    
    if (storedUserPermissions) {
      try {
        const parsedUserPermissions = JSON.parse(storedUserPermissions);
        console.log('Restoring userPermissions from localStorage:', parsedUserPermissions);
        setUserPermissions(parsedUserPermissions);
      } catch (e) {
        console.error('Error parsing stored user permissions on mount:', e);
      }
    }
    
    if (storedUserData) {
      try {
        const parsedUserData = JSON.parse(storedUserData);
        console.log('Restoring user data from localStorage:', parsedUserData);
        setUser(parsedUserData);
        // Also restore roles from user data if available
        if (parsedUserData.roles && Array.isArray(parsedUserData.roles)) {
          const roleNames = parsedUserData.roles.map(role => role.name);
          setUserRoles(roleNames);
        }
      } catch (e) {
        console.error('Error parsing stored user data on mount:', e);
      }
    }
    
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Inactive customer 403 → align local `user.is_active` with backend (avoid spamming other errors)
  useEffect(() => {
    const onInactive = () => {
      setUser((prev) => {
        let base = prev;
        if (!base) {
          const raw = localStorage.getItem('user_data');
          if (raw) {
            try {
              base = JSON.parse(raw);
            } catch (parseErr) {
              base = null;
            }
          }
        }
        if (!base) return prev;
        const next = { ...base, is_active: false };
        localStorage.setItem('user_data', JSON.stringify(next));
        return next;
      });
    };
    window.addEventListener('gofar:customer-account-inactive', onInactive);
    return () => window.removeEventListener('gofar:customer-account-inactive', onInactive);
  }, []);

  // Login function
  const login = useCallback(async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Login attempt to:', `${apiURL}/auth/`);
      const response = await fetch(`${apiURL}/auth/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      console.log('Login response status:', response.status);

      if (response.ok) {
        try {
          const data = await response.json();
          console.log('Login response data:', data);
          
          // Store tokens from new response format
          localStorage.setItem('access_token', data.access);
          localStorage.setItem('refresh_token', data.refresh);
          localStorage.setItem('Authorization', `Bearer ${data.access}`);
          
          // Set user data from new response format
          setUser(data.user);
          
          // Extract role names from roles array
          if (data.user.roles && Array.isArray(data.user.roles)) {
            const roleNames = data.user.roles.map(role => role.name);
            setUserRoles(roleNames);
            // Store userRoles and user data in localStorage for persistence
            localStorage.setItem('user_roles', JSON.stringify(roleNames));
          } else {
            // No roles found in user data
            console.warn('No roles found in user data');
            setUserRoles([]);
          }
          localStorage.setItem('user_data', JSON.stringify(data.user));
          
          // Fetch permissions from the new API (don't fail login if this fails)
          try {
            await fetchUserPermissions(data.access);
          } catch (error) {
            console.warn('Failed to fetch user permissions, but continuing with login:', error);
          }
          
          setIsAuthenticated(true);
          
          console.log('Login successful, user set:', data.user);
          console.log('Authentication state:', { isAuthenticated: true, user: data.user, userRoles, userPermissions });
          
          // Log fallback permissions for debugging
          if (userRoles && userRoles.length > 0 && (!userPermissions || userPermissions.length === 0)) {
            const fallbackPermissions = getFallbackPermissions(userRoles);
            console.log('Fallback permissions for user roles:', userRoles, 'are:', fallbackPermissions);
          }
          
          return { success: true, data };
        } catch (jsonError) {
          console.warn('JSON parsing error in login response:', jsonError.message);
          setError('Invalid response format from server');
          return { success: false, error: 'Invalid response format from server' };
        }
      } else {
        try {
          const errorData = await response.json();
          console.log('Login failed with error:', errorData);
          const extracted = extractRawMessageFromData(errorData);
          const errMsg = extracted != null && String(extracted).trim() !== '' ? extracted : null;
          setError(errMsg);
          return { success: false, error: errMsg };
        } catch (jsonError) {
          console.warn('JSON parsing error in login error response:', jsonError.message);
          const fallback = `Login failed with status: ${response.status}`;
          setError(fallback);
          return { success: false, error: fallback };
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error occurred');
      return { success: false, error: 'Network error occurred' };
    } finally {
      setLoading(false);
    }
  }, [fetchUserPermissions]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (token && refreshToken) {
        await fetch(`${apiURL}/auth/logout/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state and storage
      clearLocalStoragePreservingLanguage();
      setUser(null);
      setUserRoles([]);
      setUserPermissions([]);
      setIsAuthenticated(false);
      
      // Check if we're on a customer route to redirect to the correct login page
      const currentPath = window.location.pathname;
      const isCustomerRoute = currentPath.startsWith('/customer-');
      const redirectPath = isCustomerRoute ? '/customer-login' : '/Login';
      navigate(redirectPath, { replace: true });
    }
  }, [navigate]);

  // Fallback permission mapping for when new API is not available
  const getFallbackPermissions = useCallback((userRoles) => {
    const fallbackPermissions = [];
    
    userRoles.forEach(role => {
      switch (role) {
        case ROLES.SUPER_ADMIN:
          // Super admin gets all new individual view permissions
          fallbackPermissions.push(
            // Invoice Views (4 permissions)
            'create_invoices', 'generate_invoices_for_shipments', 'regenerate_invoices', 'update_invoices_payment_status',
            // Shipment Views (5 permissions)
            'shipment_upload', 'recalculate_shipments_price', 'shipments_bulk_update', 'shipments_bulk_tags', 'receive_shipments', 'shipments_bulk_delete',
            // Payment Views (2 permissions)
            'add_shipment_payment', 'add_invoice_payment',
            // Settings Views (8 permissions)
            'get_site_settings', 'update_site_settings', 'setting_detail', 'get_all_setting_files', 'get_setting_file', 'upload_setting_file', 'update_setting_file', 'delete_setting_file',
            // WhatsApp Views (3 permissions)
            'bulk_send_shipments_whatsapp_message', 'send_whatsapp_shipment_notification', 'send_whatsapp_shipment_notification_test',
            // Authentication Views (9 permissions)
            'custom_token_obtain_pair', 'logout', 'user_role_management', 'auth_verify', 'user_permissions', 'assign_permissions', 'get_role_permissions', 'assign_role_to_user', 'revoke_role',
            // Dashboard Views (1 permission)
            'dashboard_analytics'
          );
          break;
        case ROLES.OPERATIONS_MANAGER:
          // Operations Manager gets most operational permissions
          fallbackPermissions.push(
            // Shipment Views (5 permissions)
            'shipment_upload', 'recalculate_shipments_price', 'shipments_bulk_update', 'shipments_bulk_tags', 'receive_shipments', 'shipments_bulk_delete',
            // Invoice Views (4 permissions)
            'create_invoices', 'generate_invoices_for_shipments', 'regenerate_invoices', 'update_invoices_payment_status',
            // Payment Views (2 permissions)
            'add_shipment_payment', 'add_invoice_payment',
            // Settings Views (6 permissions - no upload/delete)
            'get_site_settings', 'update_site_settings', 'setting_detail', 'get_all_setting_files', 'get_setting_file', 'update_setting_file',
            // WhatsApp Views (3 permissions)
            'bulk_send_shipments_whatsapp_message', 'send_whatsapp_shipment_notification', 'send_whatsapp_shipment_notification_test',
            // Dashboard Views (1 permission)
            'dashboard_analytics'
          );
          break;
        case ROLES.CUSTOMER_SERVICE:
          // Customer Service gets customer-focused permissions
          fallbackPermissions.push(
            // Shipment Views (3 permissions - no bulk delete, recalculate)
            'shipment_upload', 'shipments_bulk_update', 'shipments_bulk_tags', 'receive_shipments',
            // Invoice Views (3 permissions - no regenerate)
            'create_invoices', 'generate_invoices_for_shipments', 'update_invoices_payment_status',
            // Payment Views (2 permissions)
            'add_shipment_payment', 'add_invoice_payment',
            // Settings Views (3 permissions - basic settings)
            'get_site_settings', 'update_site_settings', 'setting_detail',
            // WhatsApp Views (2 permissions - no test)
            'bulk_send_shipments_whatsapp_message', 'send_whatsapp_shipment_notification',
            // Dashboard Views (1 permission)
            'dashboard_analytics'
          );
          break;
        case ROLES.WAREHOUSE_STAFF:
          // Warehouse Staff gets warehouse-focused permissions
          fallbackPermissions.push(
            // Shipment Views (4 permissions - no bulk delete)
            'shipment_upload', 'recalculate_shipments_price', 'shipments_bulk_update', 'shipments_bulk_tags', 'receive_shipments',
            // Invoice Views (1 permission)
            'create_invoices',
            // Settings Views (2 permissions - basic settings)
            'get_site_settings', 'update_site_settings',
            // WhatsApp Views (1 permission)
            'send_whatsapp_shipment_notification',
            // Dashboard Views (1 permission)
            'dashboard_analytics'
          );
          break;
        case ROLES.FINANCE_OFFICER:
          // Finance Officer gets financial permissions
          fallbackPermissions.push(
            // Invoice Views (4 permissions)
            'create_invoices', 'generate_invoices_for_shipments', 'regenerate_invoices', 'update_invoices_payment_status',
            // Payment Views (2 permissions)
            'add_shipment_payment', 'add_invoice_payment',
            // Settings Views (3 permissions - basic settings)
            'get_site_settings', 'update_site_settings', 'setting_detail',
            // Dashboard Views (1 permission)
            'dashboard_analytics'
          );
          break;
        case ROLES.SERVICE_MANAGER:
          // Service Manager gets service-focused permissions
          fallbackPermissions.push(
            // Settings Views (5 permissions - service management)
            'get_site_settings', 'update_site_settings', 'setting_detail', 'get_all_setting_files', 'update_setting_file',
            // Dashboard Views (1 permission)
            'dashboard_analytics'
          );
          break;
        case ROLES.SYSTEM_ADMIN:
          // System Admin gets system management permissions
          fallbackPermissions.push(
            // Authentication Views (9 permissions)
            'custom_token_obtain_pair', 'logout', 'user_role_management', 'auth_verify', 'user_permissions', 'assign_permissions', 'get_role_permissions', 'assign_role_to_user', 'revoke_role',
            // Settings Views (8 permissions)
            'get_site_settings', 'update_site_settings', 'setting_detail', 'get_all_setting_files', 'get_setting_file', 'upload_setting_file', 'update_setting_file', 'delete_setting_file',
            // Dashboard Views (1 permission)
            'dashboard_analytics'
          );
          break;
        case ROLES.CUSTOMER:
          // Customer gets limited permissions
          fallbackPermissions.push(
            // Dashboard Views (1 permission)
            'dashboard_analytics'
          );
          break;
        case ROLES.DRIVER:
          // Driver gets delivery-focused permissions
          fallbackPermissions.push(
            // Shipment Views (2 permissions - basic shipment operations)
            'shipment_upload', 'receive_shipments',
            // Dashboard Views (1 permission)
            'dashboard_analytics'
          );
          break;
      }
    });
    
    return [...new Set(fallbackPermissions)]; // Remove duplicates
  }, []);

  // Check if user has permission for a specific action
  const hasPermission = useCallback((permission) => {
    // Super admin has all permissions
    if (userRoles.includes(ROLES.SUPER_ADMIN)) {
      return true;
    }
    
    // If we have permissions from the new API, use them
    if (userPermissions && userPermissions.length > 0) {
      return userPermissions.includes(permission);
    }
    
    // Fallback: if no permissions are available, use role-based mapping
    if (userRoles && userRoles.length > 0) {
      const fallbackPermissions = getFallbackPermissions(userRoles);
      return fallbackPermissions.includes(permission);
    }
    
    return false;
  }, [userRoles, userPermissions, getFallbackPermissions]);

  // Check if user has any of the required permissions
  const hasAnyPermission = useCallback((permissionList) => {
    // Super admin has all permissions
    if (userRoles.includes(ROLES.SUPER_ADMIN)) return true;
    
    // If we have permissions from the new API, use them
    if (userPermissions && userPermissions.length > 0) {
      return permissionList.some(permission => userPermissions.includes(permission));
    }
    
    // Fallback: if no permissions are available, use role-based mapping
    if (userRoles && userRoles.length > 0) {
      const fallbackPermissions = getFallbackPermissions(userRoles);
      return permissionList.some(permission => fallbackPermissions.includes(permission));
    }
    
    return false;
  }, [userRoles, userPermissions, getFallbackPermissions]);

  // Check if user has all of the required permissions
  const hasAllPermissions = useCallback((permissionList) => {
    // Super admin has all permissions
    if (userRoles.includes(ROLES.SUPER_ADMIN)) return true;
    
    // If we have permissions from the new API, use them
    if (userPermissions && userPermissions.length > 0) {
      return permissionList.every(permission => userPermissions.includes(permission));
    }
    
    // Fallback: if no permissions are available, use role-based mapping
    if (userRoles && userRoles.length > 0) {
      const fallbackPermissions = getFallbackPermissions(userRoles);
      return permissionList.every(permission => fallbackPermissions.includes(permission));
    }
    
    return false;
  }, [userRoles, userPermissions, getFallbackPermissions]);

  // Check if user has any of the required roles
  const hasRole = useCallback((requiredRoles) => {
    if (!userRoles || userRoles.length === 0) return false;
    
    // Super admin has all roles
    if (userRoles.includes(ROLES.SUPER_ADMIN)) return true;
    
    // Check if user has any of the required roles
    return userRoles.some(role => requiredRoles.includes(role));
  }, [userRoles]);

  // Check if user can access their own data
  const canAccessOwnData = useCallback((dataOwnerId) => {
    if (!user || !dataOwnerId) return false;
    
    // Super admin can access all data
    if (userRoles.includes(ROLES.SUPER_ADMIN)) return true;
    
    // Users can access their own data
    return user.id === dataOwnerId;
  }, [user, userRoles]);

  // Get user's branch information
  const getUserBranch = useCallback(() => {
    return user && user.branch ? user.branch : null;
  }, [user]);

  // Get user's full name
  const getUserFullName = useCallback(() => {
    if (!user) return '';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }, [user]);

  // Get user's full name in Arabic
  const getUserFullNameAr = useCallback(() => {
    if (!user) return '';
    return `${user.first_name_ar || ''} ${user.last_name_ar || ''}`.trim();
  }, [user]);

  // Get user's display name (prefer Arabic if available, fallback to English)
  const getUserDisplayName = useCallback(() => {
    const arabicName = getUserFullNameAr();
    const englishName = getUserFullName();
    return arabicName || englishName || (user && user.username) || '';
  }, [user, getUserFullNameAr, getUserFullName]);

  // Get user's primary role (first role or super_admin if present)
  const getPrimaryRole = useCallback(() => {
    if (!userRoles || userRoles.length === 0) return null;
    
    // Return super_admin if present, otherwise return first role
    if (userRoles.includes(ROLES.SUPER_ADMIN)) {
      return ROLES.SUPER_ADMIN;
    }
    return userRoles[0];
  }, [userRoles]);

  // Get all user roles
  const getAllUserRoles = useCallback(() => {
    return userRoles || [];
  }, [userRoles]);

  // Check if user has a specific role
  const hasSpecificRole = useCallback((roleName) => {
    return userRoles && userRoles.includes(roleName);
  }, [userRoles]);

  // Refresh token function - defined after logout to avoid circular dependency
  const refreshToken = useCallback(async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        throw new Error('No refresh token');
      }

      const response = await fetch(`${apiURL}/auth/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshTokenValue }),
      });

      if (response.ok) {
        const data = await response.json();
        // Handle new response format with access token
        localStorage.setItem('access_token', data.access);
        localStorage.setItem('Authorization', `Bearer ${data.access}`);
        return true;
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Call logout directly to avoid circular dependency
      clearLocalStoragePreservingLanguage();
      setUser(null);
      setUserRoles([]);
      setUserPermissions([]);
      setIsAuthenticated(false);
      window.location.href = '/login';
      return false;
    }
  }, []);

  // Memoize the primary role to avoid recalculating on every render
  const primaryRole = useMemo(() => getPrimaryRole(), [getPrimaryRole]);

  const value = useMemo(() => ({
    user,
    userRoles, // New: array of user roles
    userPermissions, // New: array of user permissions
    userRole: getPrimaryRole(), // Backward compatibility: primary role
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    hasPermission,
    hasAnyPermission, // New: check if user has any of the required permissions
    hasAllPermissions, // New: check if user has all of the required permissions
    hasRole,
    canAccessOwnData,
    refreshToken,
    getUserBranch,
    getUserFullName,
    getUserFullNameAr,
    getUserDisplayName,
    getPrimaryRole,
    getAllUserRoles,
    hasSpecificRole,
    fetchUserPermissions, // New: function to fetch permissions
    refreshUserFromVerify,
    ROLES,
    PERMISSIONS,
  }), [
    user,
    userRoles,
    userPermissions,
    primaryRole,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    canAccessOwnData,
    refreshToken,
    getUserBranch,
    getUserFullName,
    getUserFullNameAr,
    getUserDisplayName,
    getPrimaryRole,
    getAllUserRoles,
    hasSpecificRole,
    fetchUserPermissions,
    refreshUserFromVerify,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// PropTypes validation
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom hook to use the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
